import { ActuatorStateI, DetectorStateI } from '../redux/types/app';
import { getActuatorNumberOfChannels } from './ActuatorChannels';

export const MAX_CONNECTED_ACTUATORS_PER_DETECTOR = 100;

const toUniqueIds = (ids: number[] = []) => Array.from(new Set(ids));

export const getDetectorConnectedActuatorIds = (detector?: DetectorStateI): number[] => {
    if (detector?.connectedActuatorChannels !== undefined) {
        return toUniqueIds(detector.connectedActuatorChannels
            .filter((connection) => connection.channels.some((channel) => Number.isInteger(channel) && channel > 0))
            .map((connection) => connection.actuatorId)).slice(0, MAX_CONNECTED_ACTUATORS_PER_DETECTOR);
    }
    return toUniqueIds(detector?.connectedActuatorIds ?? []).slice(0, MAX_CONNECTED_ACTUATORS_PER_DETECTOR);
};

export const getDetectorActuatorChannels = (detector: DetectorStateI | undefined, actuator: ActuatorStateI): number[] => {
    const channelCount = getActuatorNumberOfChannels(actuator);
    if (detector?.connectedActuatorChannels !== undefined) {
        return toUniqueIds(detector.connectedActuatorChannels
            .filter((connection) => connection.actuatorId === actuator.id)
            .flatMap((connection) => connection.channels))
            .filter((channel) => Number.isInteger(channel) && channel > 0 && channel <= channelCount)
            .sort((first, second) => first - second);
    }
    return getDetectorConnectedActuatorIds(detector).includes(actuator.id ?? -1)
        ? Array.from({ length: channelCount }, (_, index) => index + 1)
        : [];
};

export const normalizeDetectorConnections = (detector: DetectorStateI, actuators: ActuatorStateI[]): DetectorStateI => {
    const connectedActuatorChannels = actuators.flatMap((actuator) => {
        const channels = getDetectorActuatorChannels(detector, actuator);
        return actuator.id !== undefined && channels.length > 0
            ? [{ actuatorId: actuator.id, channels }]
            : [];
    }).slice(0, MAX_CONNECTED_ACTUATORS_PER_DETECTOR);
    return {
        ...detector,
        connectedActuatorChannels,
        connectedActuatorIds: connectedActuatorChannels.map((connection) => connection.actuatorId),
        state: connectedActuatorChannels.length > 0 ? 'ok' : 'error'
    };
};

export const isActuatorConnectedToDetector = (detector: DetectorStateI | undefined, actuatorId: number): boolean => {
    return getDetectorConnectedActuatorIds(detector).includes(actuatorId);
};

export const getDetectorState = (detector?: DetectorStateI): 'ok' | 'error' => {
    return getDetectorConnectedActuatorIds(detector).length > 0 ? 'ok' : 'error';
};

export const connectActuatorToDetector = (detector: DetectorStateI, actuatorId: number, channels: number[]): { detector: DetectorStateI; connected: boolean } => {
    const current = getDetectorConnectedActuatorIds(detector);
    const validChannels = toUniqueIds(channels).filter((channel) => Number.isInteger(channel) && channel > 0)
        .sort((first, second) => first - second);
    if (validChannels.length === 0 || (!current.includes(actuatorId) && current.length >= MAX_CONNECTED_ACTUATORS_PER_DETECTOR)) {
        return { detector: { ...detector, connectedActuatorIds: current }, connected: false };
    }

    const connectedActuatorChannels = [
        ...(detector.connectedActuatorChannels ?? []).filter((connection) => connection.actuatorId !== actuatorId),
        { actuatorId, channels: validChannels }
    ];
    return {
        detector: { ...detector, connectedActuatorChannels, connectedActuatorIds: connectedActuatorChannels.map((connection) => connection.actuatorId), state: 'ok' },
        connected: true
    };
};

export const disconnectActuatorFromDetector = (detector: DetectorStateI, actuatorId: number): DetectorStateI => {
    const nextConnectedActuatorIds = getDetectorConnectedActuatorIds(detector).filter((id) => id !== actuatorId);
    return {
        ...detector,
        connectedActuatorIds: nextConnectedActuatorIds,
        ...(detector.connectedActuatorChannels !== undefined ? {
            connectedActuatorChannels: detector.connectedActuatorChannels.filter((connection) => connection.actuatorId !== actuatorId)
        } : {}),
        state: nextConnectedActuatorIds.length > 0 ? 'ok' : 'error'
    };
};
